import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, FlatList, ActivityIndicator, RefreshControl } from 'react-native';
import * as Location from 'expo-location';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from './DirectionalIcon';
import { colors, spacing, typography } from '../theme';
import { useAppContext } from '../contexts/AppContext';
import { VisitTiming } from './visits/VisitTiming';
import { useLocale } from '../contexts/LocaleContext';
import { apiService, Visit, VisitStatus } from '../services/api';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { haversineDistanceMeters, GeoPoint } from '../utils/geo.util';
import { SortFilterModal, SortField, buildCustomFieldFiltersParam } from './data-lists/SortFilterModal';

interface VisitsListProps {
  campaignId?: string;
  filterAssignedToMe?: boolean;
}

// 'active' (the default) means the non-terminal, currently-actionable
// statuses — matches how VisitCampaignsListScreen's own active-only default
// is scoped, so both lists share the same notion of "active". 'all' is one
// tap away for anyone who needs the full history.
const ACTIVE_STATUSES: VisitStatus[] = ['pending', 'scheduled', 'in_progress'];
const STATUS_FILTERS: Array<VisitStatus | 'all' | 'active'> = ['active', 'all', 'pending', 'scheduled', 'in_progress', 'completed', 'cancelled'];

// Matches VISIT_FIXED_FIELD_PATHS in visits.service.ts, resolved server-side
// the same way a genuine customColumns-sourced field is — so these get the
// exact same "pick from what's actually there" checklist. Status also has
// its own dedicated STATUS_FILTERS chips above; this is just a second,
// consistent way to reach the same filter, not a conflicting one.
const VISIT_SORT_FIELDS: SortField[] = [
  { id: 'status', label: 'Status', filterable: true },
  { id: 'priority', label: 'Priority', filterable: true },
  { id: 'assignedTo', label: 'Assigned To', filterable: true },
  { id: 'departmentId', label: 'Department', filterable: true },
  { id: 'scheduledFor', label: 'Scheduled For', filterable: false },
  { id: 'createdAt', label: 'Date Created', filterable: false },
];

const statusColor = (status: VisitStatus): string => {
  switch (status) {
    case 'completed':
      return colors.status.success as string;
    case 'missed':
    case 'cancelled':
      return colors.status.error as string;
    case 'in_progress':
      return colors.status.info as string;
    case 'rescheduled':
      return colors.status.warning as string;
    default:
      return colors.text.secondary as string;
  }
};

const statusIcon = (status: VisitStatus): keyof typeof Ionicons.glyphMap => {
  switch (status) {
    case 'completed': return 'checkmark-circle';
    case 'missed': return 'close-circle';
    case 'cancelled': return 'ban';
    case 'in_progress': return 'walk';
    case 'rescheduled': return 'calendar';
    case 'scheduled': return 'time';
    default: return 'ellipse-outline';
  }
};

const priorityColor = (priority: Visit['priority']): string => {
  switch (priority) {
    case 'urgent':
      return colors.status.error as string;
    case 'high':
      return colors.status.warning as string;
    case 'low':
      return colors.text.secondary as string;
    default:
      return colors.primary as string;
  }
};

export const VisitsList: React.FC<VisitsListProps> = ({ campaignId, filterAssignedToMe }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  // Two columns once the list is wide (desktop), measured on the list itself
  const [listWidth, setListWidth] = useState(0);
  const columns = listWidth >= 900 ? 2 : 1;
  const { navigateTo } = useAppContext();
  const { t } = useLocale();

  const [visits, setVisits] = useState<Visit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<VisitStatus | 'all' | 'active'>('active');
  // contactId -> display name, resolved via a single batch lookup per load
  // rather than each row rendering a raw id fragment (e.g. "…a91f2c") —
  // confirmed real usability gap: the old row had no way to identify WHICH
  // contact a visit was for without opening it.
  const [contactNames, setContactNames] = useState<Record<string, string>>({});
  // Field agents need a sense of which visit to do first — sort nearest-
  // first once a GPS fix resolves; falls back to chronological (scheduledFor
  // ascending, unscheduled last) if permission is denied or the fix fails,
  // so the list is never left in the backend's arbitrary default order.
  const [agentPosition, setAgentPosition] = useState<GeoPoint | null>(null);
  const locationRequestedRef = useRef(false);

  // Sort/filter by extra fields — see VendorsListScreen.tsx for the
  // reference wiring. An explicit sortBy here overrides the distance/date
  // smart-default sort below (see sortedVisits), matching "user's explicit
  // choice wins over an automatic default" once they've made one.
  const [showSortFilter, setShowSortFilter] = useState(false);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [customFieldFilters, setCustomFieldFilters] = useState<Record<string, string[]>>({});
  const [customFieldKeys, setCustomFieldKeys] = useState<string[]>([]);
  const [fieldValueOptions, setFieldValueOptions] = useState<Record<string, string[]>>({});
  const [loadingFieldValues, setLoadingFieldValues] = useState<string | null>(null);
  const customFieldActiveCount = Object.values(customFieldFilters).filter((v) => v.length > 0).length;

  useEffect(() => {
    apiService.getVisitCustomFieldKeys().then((res) => {
      if (res.statusCode === 1000 && res.data) setCustomFieldKeys(res.data);
    }).catch(() => {});
  }, []);

  const handleExpandField = useCallback((fieldId: string) => {
    setLoadingFieldValues(fieldId);
    apiService.getVisitCustomFieldValues(fieldId).then((res) => {
      const data = res.data;
      if (res.statusCode === 1000 && data) {
        setFieldValueOptions((prev) => ({ ...prev, [fieldId]: data }));
      }
    }).finally(() => setLoadingFieldValues(null));
  }, []);

  useEffect(() => {
    if (locationRequestedRef.current) return;
    locationRequestedRef.current = true;
    (async () => {
      try {
        const { status } = await Location.requestForegroundPermissionsAsync();
        if (status !== 'granted') return;
        const location = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Balanced });
        setAgentPosition({ latitude: location.coords.latitude, longitude: location.coords.longitude });
      } catch (err) {
        console.warn('[VisitsList] Failed to resolve agent location for distance sort:', err);
      }
    })();
  }, []);

  const sortedVisits = useMemo(() => {
    const list = [...visits];
    // An explicit user-chosen sort (via the Sort & Filter Fields modal)
    // overrides the automatic distance/date default — `visits` already
    // arrives in that server-chosen order (see loadVisits).
    if (sortBy) return list;
    if (agentPosition) {
      const distanceOf = (v: Visit): number => {
        const { latitude, longitude } = v.targetLocation || {};
        if (latitude == null || longitude == null) return Infinity;
        return haversineDistanceMeters(agentPosition, { latitude, longitude });
      };
      list.sort((a, b) => distanceOf(a) - distanceOf(b));
      return list;
    }
    list.sort((a, b) => {
      if (!a.scheduledFor && !b.scheduledFor) return 0;
      if (!a.scheduledFor) return 1;
      if (!b.scheduledFor) return -1;
      return new Date(a.scheduledFor).getTime() - new Date(b.scheduledFor).getTime();
    });
    return list;
  }, [visits, agentPosition]);

  const loadVisits = useCallback(async (isRefresh: boolean) => {
    if (isRefresh) setIsRefreshing(true);
    else setIsLoading(true);
    try {
      const filters: Record<string, any> = {};
      if (campaignId) filters.campaignId = campaignId;
      if (statusFilter === 'active') filters.status = ACTIVE_STATUSES.join(',');
      else if (statusFilter !== 'all') filters.status = statusFilter;
      if (sortBy) { filters.sortBy = sortBy; filters.sortOrder = sortOrder; }
      const cff = buildCustomFieldFiltersParam(customFieldFilters);
      if (cff) filters.customFieldFilters = cff;
      const res = await apiService.listVisits(filters);
      const data = Array.isArray(res.data) ? res.data : [];
      setVisits(data);

      const uniqueContactIds = Array.from(new Set(data.map((v) => v.contactId).filter(Boolean)));
      if (uniqueContactIds.length > 0) {
        try {
          const namesRes = await apiService.getContactSendTargets(uniqueContactIds);
          const map: Record<string, string> = {};
          (namesRes.data?.results || []).forEach((r) => { if (r.name) map[r.id] = r.name; });
          setContactNames(map);
        } catch (err) {
          console.warn('[VisitsList] loadContactNames error:', err);
        }
      }
    } catch (err) {
      console.warn('[VisitsList] loadVisits error:', err);
      setVisits([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [campaignId, statusFilter, sortBy, sortOrder, customFieldFilters]);

  useEffect(() => {
    loadVisits(false);
  }, [loadVisits]);

  const statusLabel = (status: VisitStatus | 'all' | 'active'): string => {
    if (status === 'all') return t('visits.filters.all');
    if (status === 'active') return t('visits.filters.active');
    return t(`visits.status.${status}` as any);
  };

  const renderRow = ({ item }: { item: Visit }) => {
    const contactLabel = contactNames[item.contactId] || `${t('visits.list.contact')} ${item.contactId.slice(-6)}`;
    return (
      <TouchableOpacity
        style={[styles.row, columns > 1 && styles.rowInGrid]}
        activeOpacity={0.7}
        onPress={() => navigateTo('visit-detail', undefined, { visitId: item._id })}
      >
        <View style={[styles.statusStripe, { backgroundColor: statusColor(item.status) }]} />
        <View style={styles.rowBody}>
          <View style={styles.rowTop}>
            <Ionicons name="person-circle-outline" size={16} color={colors.text.secondary as string} />
            <Text style={styles.contactRef} numberOfLines={1}>{contactLabel}</Text>
            {!campaignId && item.campaignId && (
              <Ionicons name="albums-outline" size={13} color={colors.primary as string} style={styles.campaignIcon} />
            )}
            <View style={[styles.priorityDot, { backgroundColor: priorityColor(item.priority) }]} />
          </View>

          {!!item.targetLocation?.address && (
            <View style={styles.addressRow}>
              <Ionicons name="location-outline" size={13} color={colors.text.secondary as string} />
              {/* Follow the interface direction, not the address's own script
                  (an Arabic address otherwise jumps to the far edge) */}
              <Text style={[styles.addressText, { textAlign: rtl.textStart }]} numberOfLines={1}>{item.targetLocation.address}</Text>
            </View>
          )}

          <View style={styles.rowBottom}>
            <View style={[styles.statusBadge, { borderColor: statusColor(item.status) }]}>
              <Ionicons name={statusIcon(item.status)} size={11} color={statusColor(item.status)} />
              <Text style={[styles.statusBadgeText, { color: statusColor(item.status) }]}>{statusLabel(item.status)}</Text>
            </View>
            {!item.scheduledFor && <Text style={styles.scheduledText}>{t('visits.list.noSchedule')}</Text>}
          </View>
          <VisitTiming visit={item} />
        </View>
        <DirectionalIcon name="chevron-forward" size={18} color={colors.text.secondary as string} />
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.filterHeaderRow}>
        <FlatList
          horizontal
          showsHorizontalScrollIndicator={false}
          data={STATUS_FILTERS}
          keyExtractor={(s) => s}
          style={styles.filterRow}
          contentContainerStyle={styles.filterRowContent}
          renderItem={({ item }) => {
            const active = item === statusFilter;
            return (
              <TouchableOpacity
                style={[styles.filterChip, active && styles.filterChipActive]}
                onPress={() => setStatusFilter(item)}
              >
                <Text style={[styles.filterChipText, active && styles.filterChipTextActive]}>{statusLabel(item)}</Text>
              </TouchableOpacity>
            );
          }}
        />
        <TouchableOpacity
          style={[styles.sortFilterButton, (customFieldActiveCount > 0 || !!sortBy) && styles.sortFilterButtonActive]}
          onPress={() => setShowSortFilter(true)}
        >
          <Ionicons name="filter-outline" size={18} color={(customFieldActiveCount > 0 || !!sortBy) ? colors.white : (colors.text.secondary as string)} />
        </TouchableOpacity>
      </View>

      {isLoading ? (
        <View style={styles.centered}>
          <ActivityIndicator color={colors.primary as string} />
        </View>
      ) : (
        <FlatList
          key={`visits-${columns}`}
          numColumns={columns}
          columnWrapperStyle={columns > 1 ? styles.gridRow : undefined}
          onLayout={(e) => setListWidth(e.nativeEvent.layout.width)}
          data={sortedVisits}
          keyExtractor={(v) => v._id}
          renderItem={renderRow}
          contentContainerStyle={sortedVisits.length === 0 ? styles.emptyListContent : styles.listContent}
          refreshControl={
            <RefreshControl refreshing={isRefreshing} onRefresh={() => loadVisits(true)} tintColor={colors.primary as string} />
          }
          ListEmptyComponent={
            <View style={styles.emptyState}>
              <Ionicons name="walk-outline" size={40} color={colors.text.secondary as string} />
              <Text style={styles.emptyStateText}>{t('visits.list.empty')}</Text>
            </View>
          }
        />
      )}

      <SortFilterModal
        visible={showSortFilter}
        onClose={() => setShowSortFilter(false)}
        sortFields={VISIT_SORT_FIELDS}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onChangeSort={(field, order) => { setSortBy(field); setSortOrder(order); }}
        customFieldKeys={customFieldKeys}
        customFieldFilters={customFieldFilters}
        onChangeCustomFieldFilters={setCustomFieldFilters}
        fieldValueOptions={fieldValueOptions}
        loadingFieldValues={loadingFieldValues}
        onExpandField={handleExpandField}
        labels={{ modalTitle: 'Sort & Filter Visits' }}
      />
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1 },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },

  // Room above the chips (they touched the header and looked cut off)
  filterHeaderRow: { flexDirection: 'row', alignItems: 'center', paddingTop: spacing.sm },
  filterRow: { flexGrow: 1, marginBottom: spacing.sm },
  filterRowContent: { paddingHorizontal: spacing.md, paddingVertical: 2, gap: spacing.xs, alignItems: 'center' },
  sortFilterButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: spacing.md,
    marginBottom: spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.border as string,
  },
  sortFilterButtonActive: {
    backgroundColor: colors.primary as string,
    borderColor: colors.primary as string,
  },
  filterChip: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 16,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
    marginRight: spacing.xs,
  },
  filterChipActive: { backgroundColor: colors.primary as string, borderColor: colors.primary as string },
  filterChipText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string },
  filterChipTextActive: { color: colors.white as string, fontFamily: typography.fontFamily.semibold },

  listContent: { paddingHorizontal: spacing.md, paddingBottom: spacing.xl },
  emptyListContent: { flexGrow: 1, paddingHorizontal: spacing.md },
  gridRow: { gap: spacing.sm },
  rowInGrid: { flex: 1, minWidth: 0 },

  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.surface as string,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border as string,
    marginBottom: spacing.sm,
    overflow: 'hidden',
  },
  statusStripe: { width: 4, alignSelf: 'stretch' },
  rowBody: { flex: 1, padding: spacing.md, gap: 4 },
  rowTop: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  contactRef: { flex: 1, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },
  priorityDot: { width: 8, height: 8, borderRadius: 4 },
  campaignIcon: {},
  addressRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  addressText: { flex: 1, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },
  rowBottom: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 2 },
  statusBadge: { flexDirection: 'row', alignItems: 'center', gap: 4, borderWidth: 1, borderRadius: 12, paddingVertical: 2, paddingHorizontal: spacing.sm },
  statusBadgeText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold },
  scheduledRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  scheduledText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },

  emptyState: { alignItems: 'center', justifyContent: 'center', paddingVertical: spacing.xl * 2, gap: spacing.sm },
  emptyStateText: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },
});
